/**
 * Anzeigelogik fürs Schreibbild. Pure — deshalb hier und nicht in der Komponente.
 *
 * Zwei Fragen beantwortet diese Datei:
 *
 * 1. **Welche Spalten braucht die Variantentabelle?** Eine Spalte, die bei keiner
 *    Variante einen Wert trägt, ist keine Auskunft — sie kostet nur Breite. Dass das
 *    Feld leer bleibt, sagt stattdessen der Klartext-Block darüber.
 * 2. **Was fehlt in Plenty?** Ein Attributwert, den es dort nicht gibt, kommt nicht an
 *    die Variante. Das ist bei uhlsport der Engpass (90 von 154 Farben fehlen), und es
 *    gehört an die auffälligste Stelle der Tabelle, nicht in eine Fußnote.
 */

import type { SchreibbildAttribut, SchreibbildVariante } from './typen';

export interface Spaltenbedarf {
  nummer: boolean;
  zoll: boolean;
  gewicht: boolean;
  masse: boolean;
  bild: boolean;
  /** Nur wenn sie sich zwischen den Varianten unterscheiden — sonst stehen sie im Kopf. */
  eigenschaften: boolean;
  tags: boolean;
}

/** Welche Zusatzspalten dieser Artikel wirklich braucht. Pure. */
export function spaltenBedarf(varianten: SchreibbildVariante[]): Spaltenbedarf {
  const irgendwo = (pruefe: (v: SchreibbildVariante) => boolean) => varianten.some(pruefe);

  return {
    nummer: irgendwo(v => v.nummer != null),
    zoll: irgendwo(v => !!v.zolltarifnummer),
    gewicht: irgendwo(v => v.gewichtG != null),
    masse: irgendwo(v => v.laengeMm != null || v.breiteMm != null || v.hoeheMm != null),
    bild: irgendwo(v => v.bilder.length > 0),
    eigenschaften: unterscheidetSich(varianten, v => v.eigenschaften.map(e => e.name).join('|')),
    tags: unterscheidetSich(varianten, v => v.tags.map(t => t.name).join('|')),
  };
}

/** true, wenn nicht alle Varianten denselben Wert liefern. */
function unterscheidetSich(
  varianten: SchreibbildVariante[],
  lies: (v: SchreibbildVariante) => string,
): boolean {
  if (varianten.length < 2) return false;
  const erster = lies(varianten[0]);
  return varianten.some(v => lies(v) !== erster);
}

/**
 * Die Attributwerte, die es in Plenty nicht gibt — je Wert einmal, mit Anzahl.
 *
 * Nach Attribut getrennt, weil „schwarz" bei Farbe etwas anderes ist als bei Größe und
 * die beiden Listen an verschiedene Leute gehen.
 */
export function fehlendeAttributwerte(
  varianten: SchreibbildVariante[],
): Array<{ attributId: number; rolle: SchreibbildAttribut['rolle']; wert: string; anzahl: number }> {
  const gezaehlt = new Map<string, { attributId: number; rolle: SchreibbildAttribut['rolle']; wert: string; anzahl: number }>();

  for (const variante of varianten) {
    for (const attribut of variante.attribute) {
      if (attribut.valueId != null) continue;
      const schluessel = `${attribut.attributId}|${attribut.wert}`;
      const vorhanden = gezaehlt.get(schluessel);
      if (vorhanden) vorhanden.anzahl++;
      else {
        gezaehlt.set(schluessel, {
          attributId: attribut.attributId,
          rolle: attribut.rolle,
          wert: attribut.wert,
          anzahl: 1,
        });
      }
    }
  }

  return Array.from(gezaehlt.values()).sort((a, b) => b.anzahl - a.anzahl);
}

/** Die Maße einer Variante als eine Zelle: „300 × 200 × 20". */
export function masse(variante: SchreibbildVariante): string {
  const teile = [variante.laengeMm, variante.breiteMm, variante.hoeheMm];
  if (teile.every(t => t == null)) return '–';
  return teile.map(t => (t == null ? '?' : String(t))).join(' × ');
}
